import React, { useState, useEffect } from 'react';
import { Form, Field, FormElement } from '@progress/kendo-react-form';
import { Input, TextArea } from '@progress/kendo-react-inputs';
import { MultiSelect, DropDownList } from '@progress/kendo-react-dropdowns';
import { Switch } from '@progress/kendo-react-inputs';
import { FaTimesCircle, FaPaperclip, FaFilePdf, FaFileImage, FaTimes } from 'react-icons/fa';
import { sendNotification } from '../../../data/notificationData';
import { getApiErrorDetails } from '../../../helpers/apiError';
import { getEmployeeDropdown } from '../../../data/employeeData';
import { getGroups, Group } from '../../../data/groupData';
import { getFormsDropdown, DropdownOption } from '../../../data/formData';
import { dropDown } from '../../../data/interfaces';
import { requiredValidator } from '../Validators';

interface AddNotificationFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

const AddNotificationForm: React.FC<AddNotificationFormProps> = ({ onClose, onSuccess }) => {
  const [employees, setEmployees] = useState<dropDown[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<dropDown[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [forms, setForms] = useState<DropdownOption[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [selectedForm, setSelectedForm] = useState<number | null>(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [recipientType, setRecipientType] = useState<'employee' | 'group'>('employee');
  const [mandatoryRead, setMandatoryRead] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadEmployees();
    loadGroups();
    loadForms();
  }, []);

  const loadEmployees = async () => {
    try {
      const data = await getEmployeeDropdown('');
      setEmployees(data);
      setFilteredEmployees(data);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadGroups = async () => {
    try {
      const data = await getGroups();
      setGroups(data);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  const loadForms = async () => {
    try {
      const data = await getFormsDropdown();
      setForms(data);
    } catch (error) {
      console.error('Error loading forms:', error);
    }
  };

  const handleEmployeeChange = (event: any) => {
    setSelectedEmployees(event.value.map((e: dropDown) => e.value));
  };

  const handleEmployeeFilterChange = (event: any) => {
    const filterValue = event.filter.value.toLowerCase();
    
    if (filterValue) {
      const filtered = employees.filter((employee) =>
        employee.text.toLowerCase().includes(filterValue)
      );
      // Always include already selected employees in the filtered list
      const selectedItems = employees.filter(e => selectedEmployees.includes(e.value));
      const combined = [...filtered];
      selectedItems.forEach(item => {
        if (!combined.find(c => c.value === item.value)) {
          combined.push(item);
        }
      });
      setFilteredEmployees(combined);
    } else {
      setFilteredEmployees(employees);
    }
  };

  // Images and PDFs are what the app can show; anything else couldn't be opened
  // on the phone, so it isn't offered.
  const acceptedTypes = 'image/*,application/pdf';

  const handleAttachmentsPicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []).filter(
      (file) => file.type.startsWith('image/') || file.type === 'application/pdf'
    );
    setAttachments((prev) => [
      ...prev,
      ...picked.filter((file) => !prev.some((p) => p.name === file.name && p.size === file.size)),
    ]);
    // Clear the input so picking the same file again after removing it works.
    event.target.value = '';
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const formatSize = (bytes: number) =>
    bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

  const handleGroupChange = (event: any) => {
    setSelectedGroups((event.value as Group[]).map((g) => g.groupId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject.trim()) {
      alert('Please enter a subject');
      return;
    }

    if (!message.trim()) {
      alert('Please enter a message');
      return;
    }

    if (recipientType === 'employee' && selectedEmployees.length === 0) {
      alert('Please select at least one employee');
      return;
    }

    if (recipientType === 'group' && selectedGroups.length === 0) {
      alert('Please select at least one group');
      return;
    }

    setSending(true);
    try {
      // The API creates one notification per employee (or per group) and
      // shares the attachments between them.
      await sendNotification(
        {
          recipientEmployeeIds: recipientType === 'employee' ? selectedEmployees : [],
          recipientGroupIds: recipientType === 'group' ? selectedGroups : [],
          subject: subject.trim(),
          message: message.trim(),
          formId: selectedForm,
          mandatoryRead,
        },
        attachments
      );

      onSuccess();
    } catch (error) {
      console.error('Error creating notification:', error);
      const { message: reason, details } = getApiErrorDetails(error, 'Failed to send the notification.');
      alert([reason, ...details].join('\n'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="form__title__container">
        <h1 className="form__title">Add New Notification</h1>
        <FaTimesCircle
          onClick={onClose}
          style={{ cursor: 'pointer', fontSize: '1.5rem', color: 'white' }}
        />
      </div>

      <div className="form__wrapper">
        <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
            Send To:
          </label>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="radio"
                name="recipientType"
                value="employee"
                checked={recipientType === 'employee'}
                onChange={() => {
                  setRecipientType('employee');
                  setSelectedGroups([]);
                }}
              />
              Individual Employee(s)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="radio"
                name="recipientType"
                value="group"
                checked={recipientType === 'group'}
                onChange={() => {
                  setRecipientType('group');
                  setSelectedEmployees([]);
                }}
              />
              Group(s)
            </label>
          </div>

          {recipientType === 'employee' ? (
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Select Employees:
              </label>
              <MultiSelect
                data={filteredEmployees}
                textField="text"
                dataItemKey="value"
                value={filteredEmployees.filter(e => selectedEmployees.includes(e.value))}
                onChange={handleEmployeeChange}
                filterable={true}
                onFilterChange={handleEmployeeFilterChange}
                placeholder="Select employees..."
                style={{ width: '100%' }}
              />
            </div>
          ) : (
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Select Groups:
              </label>
              <MultiSelect
                data={groups}
                textField="name"
                dataItemKey="groupId"
                value={groups.filter(g => selectedGroups.includes(g.groupId))}
                onChange={handleGroupChange}
                placeholder="Select groups..."
                style={{ width: '100%' }}
              />
              {selectedGroups.length > 1 && (
                <p className='k-form-hint'>Anyone in more than one of these groups gets it once.</p>
              )}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
            Assign Form (Optional):
          </label>
          <DropDownList
            data={forms}
            textField="text"
            dataItemKey="value"
            value={selectedForm ? forms.find(f => f.value === selectedForm) : null}
            onChange={(e) => setSelectedForm(e.value ? e.value.value : null)}
            defaultItem={{ value: null, text: "Select a form..." }}
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
            Subject: *
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter notification subject..."
            maxLength={200}
            style={{
              width: '100%',
              padding: '0.5rem',
              borderRadius: '0.25rem',
              border: '1px solid #ccc',
              fontFamily: 'inherit',
              fontSize: '1rem',
            }}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
            Message: *
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter notification message..."
            rows={6}
            style={{
              width: '100%',
              padding: '0.5rem',
              borderRadius: '0.25rem',
              border: '1px solid #ccc',
              fontFamily: 'inherit',
              fontSize: '1rem',
            }}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
            Attachments (Optional):
          </label>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.4rem 0.8rem',
              border: '1px solid #ccc',
              borderRadius: '0.25rem',
              cursor: 'pointer',
              background: '#fafafa',
            }}
          >
            <FaPaperclip />
            Add images or PDFs
            <input
              type="file"
              multiple
              accept={acceptedTypes}
              onChange={handleAttachmentsPicked}
              style={{ display: 'none' }}
            />
          </label>
          {attachments.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: '0.75rem 0 0' }}>
              {attachments.map((file, index) => (
                <li
                  key={`${file.name}-${file.size}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.35rem 0.5rem',
                    marginBottom: '0.35rem',
                    border: '1px solid #e1e1e1',
                    borderRadius: '0.25rem',
                  }}
                >
                  {file.type === 'application/pdf' ? <FaFilePdf color="#b3261e" /> : <FaFileImage color="#1e2a4e" />}
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.name}
                  </span>
                  <span style={{ fontSize: '0.8rem', color: '#666' }}>{formatSize(file.size)}</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(index)}
                    aria-label={`Remove ${file.name}`}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}
                  >
                    <FaTimes />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontWeight: '600' }}>
            <Switch
              checked={mandatoryRead}
              onChange={(e) => setMandatoryRead(e.value)}
            />
            Mandatory Read
          </label>
          <p style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: '#666', marginLeft: '3rem' }}>
            When enabled, recipients must acknowledge reading this notification
          </p>
        </div>

        <div className="button__container">
          <button type="submit" className="form__edit__button accept" disabled={sending}>
            {sending ? 'Sending…' : 'Send Notification'}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default AddNotificationForm;
